import type { ReminderItem } from '@nexus/core';
import {
  Badge,
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  ListSkeleton,
  MenuList,
  Page,
  PageHeader,
  SkeletonRegion,
} from '@nexus/ui';
import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { localIsoDate } from '../i18n/format';
import { myRemindersQuery } from '../reminders/api';
import { ReminderEntry } from '../reminders/reminder-entry';
import { myTasksQuery, useCanTask } from '../tasks/api';
import { TaskEntry } from '../tasks/task-entry';
import { ApprovalEntry } from '../workflows/approval-entry';
import { approvalsQuery } from '../workflows/api';

function Section({
  title,
  count,
  more,
  children,
}: {
  title: string;
  count: number;
  more?: string | false;
  children: ReactNode;
}) {
  return (
    <Card>
      <CardHeader title={title} actions={<Badge>{count}</Badge>} />
      <MenuList aria-label={title} className="p-2">
        {children}
      </MenuList>
      {more && <p className="px-4 pb-4 text-sm text-fg-muted md:px-5">{more}</p>}
    </Card>
  );
}

/**
 * "Für mich": what waits for the person across all active modules, most urgent first. Decisions,
 * then the open tasks assigned to the person, then reminders due today or earlier, then the
 * coming ones. Each entry leads to its record,
 * where it is decided or done (docs/architecture/for-me.md).
 */
export function ForMePage() {
  const { t } = useTranslation();
  // Both lists come back empty without the permissions, so the page needs no check of its own.
  const approvals = useQuery(approvalsQuery);
  const reminders = useQuery(myRemindersQuery);
  // Tasks only where a module offers them, so the page asks nothing it cannot show.
  const canTask = useCanTask();
  const tasks = useQuery({ ...myTasksQuery, enabled: canTask });

  const decisions = approvals.data?.items ?? [];
  const today = localIsoDate(new Date());
  const due: ReminderItem[] = [];
  const coming: ReminderItem[] = [];
  for (const item of reminders.data?.items ?? []) (item.dueOn <= today ? due : coming).push(item);
  const assigned = tasks.data?.items ?? [];
  const dueTasks = assigned.filter((item) => item.dueOn !== null && item.dueOn <= today);
  const now = decisions.length + dueTasks.length + due.length;

  const loading = approvals.isPending || reminders.isPending || (canTask && tasks.isPending);
  const failed = approvals.isError || reminders.isError || tasks.isError;

  return (
    <Page>
      <PageHeader
        title={t('forMe.title')}
        description={t('forMe.description')}
        meta={
          !loading &&
          !failed && (
            <span className="text-sm text-fg-muted tabular">
              {now > 0 ? t('forMe.now', { count: now }) : t('forMe.nothingNow')}
            </span>
          )
        }
      />
      <div className="flex flex-col gap-4">
        {failed ? (
          <ErrorState
            title={t('common.loadFailed')}
            onRetry={() => {
              if (approvals.isError) void approvals.refetch();
              if (reminders.isError) void reminders.refetch();
              if (tasks.isError) void tasks.refetch();
            }}
          />
        ) : loading ? (
          <SkeletonRegion className="flex flex-col gap-4">
            <ListSkeleton rows={2} />
            <ListSkeleton rows={2} icon trailing />
          </SkeletonRegion>
        ) : decisions.length + assigned.length + due.length + coming.length === 0 ? (
          <EmptyState
            icon="circle-check"
            title={t('forMe.emptyTitle')}
            description={t('forMe.emptyDescription')}
          />
        ) : (
          <>
            {decisions.length > 0 && (
              <Section
                title={t('forMe.approvals')}
                count={decisions.length}
                more={approvals.data?.more && t('approvals.more')}
              >
                {decisions.map((item) => (
                  <ApprovalEntry key={`${item.entity}:${item.recordId}`} item={item} />
                ))}
              </Section>
            )}
            {assigned.length > 0 && (
              <Section
                title={t('forMe.tasks')}
                count={assigned.length}
                more={tasks.data?.more && t('tasks.more')}
              >
                {assigned.map((item) => (
                  <TaskEntry key={item.id} item={item} />
                ))}
              </Section>
            )}
            {due.length > 0 && (
              <Section title={t('forMe.due')} count={due.length}>
                {due.map((item) => (
                  <ReminderEntry key={item.id} item={item} />
                ))}
              </Section>
            )}
            {coming.length > 0 && (
              <Section
                title={t('forMe.coming')}
                count={coming.length}
                more={reminders.data?.more && t('reminders.more')}
              >
                {coming.map((item) => (
                  <ReminderEntry key={item.id} item={item} />
                ))}
              </Section>
            )}
          </>
        )}
      </div>
    </Page>
  );
}
